import { ATOL_DRIVER_ID, findDriver } from './drivers'
import type { DeviceProblem, DeviceState, EquipmentSnapshot, HardwareTabId, KassaConnection, VerdictState } from './types'

/**
 * Вкладка «Кассы»: карточка включённой кассы и вывод «можно ли пробивать чек» (п. 2.2, 3.4).
 * Одна касса на компьютер. Решение владельца: закрытая смена = продавать нельзя (смену открывают вручную в РМК).
 */

/** Модель и порт кассы АТОЛ, когда агент не отдал их в настройках: одни и те же для разбора настроек и для запроса «выключить». */
export const ATOL_DEFAULT_MODEL = 500
export const ATOL_DEFAULT_PORT = 1

/** Касса включена и агент принимает ей команды. */
export const kassaEnabled = (snapshot: EquipmentSnapshot): boolean =>
  snapshot.equipment !== null && snapshot.equipment.settings.enabled && snapshot.equipment.active

/** Смена закрыта: агент отдаёт `shift-closed` (блокирующая неполадка) или состояние смены `closed`. */
export function isShiftClosed(device: DeviceState | null): boolean {
  if (device === null) return false
  return device.shiftState === 'closed' || device.problems.some((problem) => problem.code === 'shift-closed')
}

export const SHIFT_CLOSED_REASON = 'смена закрыта — откройте смену в РМК'

const CONNECTION_REASON: Record<KassaConnection, string> = {
  ok: '',
  'no-device': 'касса не отвечает: проверьте кабель и питание',
  busy: 'касса занята другой программой',
  'driver-missing': 'драйвер кассы не загружается',
  error: 'нет связи с кассой',
}

export interface CashSale {
  state: VerdictState
  /** Почему нельзя или с чем можно; русский текст, слова агента — дословно. */
  reason: string | null
  /** Где причину чинят: вкладка окна или `null` (смену открывают в РМК, вне окна). */
  target: HardwareTabId | null
}

/** Итог по кассе без учёта связи с агентом (её учитывает вердикт). */
export function cashSale(snapshot: EquipmentSnapshot): CashSale {
  const driver = findDriver(snapshot, ATOL_DRIVER_ID)
  if (driver === null) return { state: 'waiting', reason: 'проверяем драйверы', target: null }
  if (driver.state !== 'ready') {
    const reason = driver.state === 'incompatible' ? 'драйвер кассы АТОЛ не подходит' : 'не установлен драйвер кассы АТОЛ'
    return { state: 'blocked', reason, target: 'drivers' }
  }
  if (!kassaEnabled(snapshot)) return { state: 'blocked', reason: 'касса не подключена', target: 'kassas' }
  const device = snapshot.device
  if (device === null) return { state: 'waiting', reason: 'проверяем связь с кассой', target: null }
  if (device.connection !== 'ok') {
    return { state: 'blocked', reason: device.problems[0]?.text ?? CONNECTION_REASON[device.connection], target: 'kassas' }
  }
  if (isShiftClosed(device)) return { state: 'blocked', reason: SHIFT_CLOSED_REASON, target: null }
  if (!device.canSell) {
    const first = device.problems.find((problem) => problem.severity === 'block')
    return { state: 'blocked', reason: first?.text ?? 'касса не готова к продаже', target: 'kassas' }
  }
  const warn = device.problems.find((problem) => problem.severity === 'warn')
  return warn === undefined ? { state: 'ready', reason: null, target: null } : { state: 'warn', reason: warn.text, target: 'kassas' }
}

export interface KassaProblemVm {
  key: string
  severity: DeviceProblem['severity']
  /** «Мешает продаже:» / «Предупреждение:» — форма, а не только цвет. */
  label: string
  glyph: '✕' | '!'
  text: string
  action: string | null
}

export interface KassaCardVm {
  title: string
  /** Как подключена: «USB» или «COM (имя)». */
  connection: string
  serial: string | null
  enabled: true
  /** Связь с кассой словом; «не проверялась», пока агент не ответил на `/v1/device`. */
  linkWord: string
  linkOk: boolean | null
  saleWord: string
  canSell: boolean
  shiftLine: string | null
  problems: readonly KassaProblemVm[]
  /** Результат последнего «Проверить связь» (строка под карточкой). */
  test: { ok: boolean; message: string; checkedAt: number } | null
}

const SHIFT_WORDS: Record<string, string> = { opened: 'открыта', closed: 'закрыта', expired: 'истекла (больше 24 часов)' }

function shiftLine(device: DeviceState | null): string | null {
  if (device === null || device.shiftState === null) return null
  const word = SHIFT_WORDS[device.shiftState] ?? device.shiftState
  const number = device.shiftNumber === null ? '' : ` №${device.shiftNumber}`
  const tail = device.shiftState === 'closed' ? ' Откройте смену в РМК.' : ''
  return `Смена${number} ${word}.${tail}`
}

/** Карточка включённой кассы; `null` — касса не включена (тогда вкладка показывает сканирование). */
export function kassaCard(snapshot: EquipmentSnapshot): KassaCardVm | null {
  if (!kassaEnabled(snapshot) || snapshot.equipment === null) return null
  const { equipment, device } = snapshot
  const sale = cashSale(snapshot)
  const problems = [...(device?.problems ?? [])]
    .sort((left, right) => Number(right.severity === 'block') - Number(left.severity === 'block'))
    .map(
      (problem): KassaProblemVm => ({
        key: problem.code,
        severity: problem.severity,
        label: problem.severity === 'block' ? 'Мешает продаже:' : 'Предупреждение:',
        glyph: problem.severity === 'block' ? '✕' : '!',
        text: problem.text,
        action: problem.action,
      }),
    )
  const serial = snapshot.scans[ATOL_DRIVER_ID]?.candidates.find((candidate) => candidate.configured)?.device?.serial ?? null
  return {
    title: equipment.title,
    connection: equipment.settings.port === 1 ? 'USB' : `COM (${equipment.settings.comFile ?? '—'})`,
    serial,
    enabled: true,
    linkWord: device === null ? 'Связь не проверялась' : device.connection === 'ok' ? 'Связь есть' : 'Нет связи',
    linkOk: device === null ? null : device.connection === 'ok',
    saleWord: sale.state === 'ready' || sale.state === 'warn' ? 'Можно продавать' : sale.state === 'waiting' ? 'Проверяем' : 'Продавать нельзя',
    canSell: sale.state === 'ready' || sale.state === 'warn',
    shiftLine: shiftLine(device),
    problems,
    test: snapshot.kassaTest,
  }
}

/** Модели кассы в списке выбора: 500 — драйвер сам определяет подключённую. */
export const ATOL_MODELS = [
  { value: 500, label: 'Определить автоматически' },
  { value: 61, label: 'АТОЛ 30Ф' },
] as const

/** Значения для `PUT /v1/equipment/atol` из подсказки сканирования или формы: порт 0 — COM, 1 — USB. */
export function atolSettingsFrom(values: Readonly<Record<string, string>>): { model: number; port: 0 | 1; comFile: string | null } {
  const model = Number(values.model)
  const port = values.port === '0' ? 0 : 1
  const comFile = (values.comFile ?? '').trim()
  return { model: Number.isFinite(model) && model > 0 ? model : 500, port, comFile: port === 0 && comFile.length > 0 ? comFile : null }
}
